import { Injectable, NgZone } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, timeout } from 'rxjs/operators';
import { CalendarPlaceSuggestion } from '../../models/entities/calendar';
import { ConfigGeoRegions } from '../../config/geo-regions';

export type PlaceSession = google.maps.places.AutocompleteSessionToken | null;

/** A street address read off a picked place; any part Google did not return is empty. */
export interface PlaceAddress
{
    line1: string;
    line2: string;
    city: string;
    region: { countryCode: string; region: string; abbreviation?: string } | null;
    zip: string;
}

type AddressPart = { long_name: string; short_name: string; types: string[] };

/** Google's address components as the fields a mailing address form holds. */
export function toPlaceAddress(parts: AddressPart[]): PlaceAddress {
    const find = (...types: string[]) => types.map(type => (parts ?? []).find(p => p.types?.includes(type))).find(Boolean);
    const street = [find('street_number')?.long_name, find('route')?.short_name].filter(Boolean).join(' ');
    const unit = find('subpremise')?.long_name ?? '';
    const state = find('administrative_area_level_1');
    const country = find('country')?.short_name || 'US';

    return {
        line1: street || find('premise')?.long_name || '',
        line2: unit ? `#${unit.replace(/^#\s*/, '')}` : '',
        city: find('locality', 'postal_town', 'sublocality_level_1', 'sublocality', 'administrative_area_level_3')?.long_name ?? '',
        region: state
                ? ConfigGeoRegions.findRegion({countryCode: country, region: state.short_name})
                ?? ConfigGeoRegions.findRegion({countryCode: country, region: state.long_name})
                : null,
        zip: find('postal_code')?.long_name ?? '',
    };
}

const PLACES_LIMIT = 5;
const PLACES_TIMEOUT_MS = 5000;

@Injectable({
    providedIn: 'root',
})
export class PlaceSuggestionsService
{
    constructor(private _zone: NgZone) {
    }

    /** One token per dialog open; null when the Maps Places library is not loaded. */
    newSession(): PlaceSession {
        const places = this.places();
        try {
            return places ? new places.AutocompleteSessionToken() : null;
        } catch {
            return null;
        }
    }

    /** Places and addresses for the typed text from the Maps Places library the app already loads; any failure is no suggestions. */
    suggest(input: string, session: PlaceSession, types?: string[]): Observable<CalendarPlaceSuggestion[]> {
        const places = this.places();
        if (!places) {
            return of([]);
        }
        return new Observable<CalendarPlaceSuggestion[]>(subscriber => {
            try {
                new places.AutocompleteService().getPlacePredictions(
                        {input, sessionToken: session ?? undefined, componentRestrictions: {country: 'US'}, ...(types ? {types} : {})},
                        (predictions, status) => this._zone.run(() => {
                            subscriber.next(status === places.PlacesServiceStatus.OK
                                    ? (predictions ?? []).filter(p => !!p?.description).slice(0, PLACES_LIMIT).map(p => ({
                                        text: p.description,
                                        mainText: p.structured_formatting?.main_text || p.description,
                                        secondaryText: p.structured_formatting?.secondary_text ?? null,
                                        placeId: p.place_id,
                                    }))
                                    : []);
                            subscriber.complete();
                        }),
                );
            } catch {
                subscriber.next([]);
                subscriber.complete();
            }
        }).pipe(
                timeout(PLACES_TIMEOUT_MS),
                catchError(() => of([])),
        );
    }

    /** The street address of a picked suggestion; null when Google cannot say. */
    address(placeId: string, session: PlaceSession): Observable<PlaceAddress | null> {
        const places = this.places();
        if (!places || !placeId) {
            return of(null);
        }
        return new Observable<PlaceAddress | null>(subscriber => {
            try {
                new places.PlacesService(document.createElement('div')).getDetails(
                        {placeId, fields: ['address_components'], sessionToken: session ?? undefined},
                        (place, status) => this._zone.run(() => {
                            subscriber.next(status === places.PlacesServiceStatus.OK && place?.address_components?.length
                                    ? toPlaceAddress(place.address_components)
                                    : null);
                            subscriber.complete();
                        }),
                );
            } catch {
                subscriber.next(null);
                subscriber.complete();
            }
        }).pipe(
                timeout(PLACES_TIMEOUT_MS),
                catchError(() => of(null)),
        );
    }

    private places(): typeof google.maps.places | null {
        return (window as Window & { google?: typeof google }).google?.maps?.places ?? null;
    }
}
